import {
  CompanyOnboardingFormData,
  OnboardingFormData,
  PersonalInvestmentProfile,
} from './SavingsFundOnboarding/types';
import {
  Address,
  CompanyOnboardingSurveyCommand,
  CompanySourceOfIncomeOption,
  ISO2CountryCode,
  OnboardingSurveyCommand,
} from './SavingsFundOnboarding/types.api';

const isPopulatedPersonalProfile = (
  profile: OnboardingFormData['personalInvestmentProfile'],
): profile is PersonalInvestmentProfile =>
  profile !== null &&
  !!profile.investmentGoals &&
  !!profile.investableAssets &&
  (profile.sourceOfIncome?.length ?? 0) > 0;

export const transformFormDataToOnboardingSurveryCommand = (
  data: OnboardingFormData,
): OnboardingSurveyCommand => {
  const answers: OnboardingSurveyCommand['answers'] = [
    {
      type: 'CITIZENSHIP',
      value: {
        type: 'COUNTRIES',
        value: data.citizenship,
      },
    },
    {
      type: 'ADDRESS',
      value: {
        type: 'ADDRESS',
        value: data.address,
      },
    },
    {
      type: 'EMAIL',
      value: {
        type: 'TEXT',
        value: data.email,
      },
    },
  ];

  if (data.phoneNumber) {
    answers.push({
      type: 'PHONE_NUMBER',
      value: {
        type: 'TEXT',
        value: data.phoneNumber,
      },
    });
  }

  if (data.pepSelfDeclaration) {
    answers.push({
      type: 'PEP_SELF_DECLARATION',
      value: {
        type: 'OPTION',
        value: data.pepSelfDeclaration,
      },
    });
  }

  // Belt-and-suspenders: the wizard clears the profile group when intent flips
  // to ONLY_VIA_COMPANY, so isPopulatedPersonalProfile alone would already do
  // the right thing today. But keeping the intent gate at the data boundary
  // means the "company-only never sends a profile" invariant doesn't rely on
  // a UI effect to stay correct — even if a future change accidentally lets a
  // populated group through, the transform still omits the profile items.
  if (
    data.investmentIntent !== 'ONLY_VIA_COMPANY' &&
    isPopulatedPersonalProfile(data.personalInvestmentProfile)
  ) {
    const { investmentGoals, investableAssets, sourceOfIncome } = data.personalInvestmentProfile;
    answers.push(
      { type: 'INVESTMENT_GOALS', value: investmentGoals },
      { type: 'INVESTABLE_ASSETS', value: { type: 'OPTION', value: investableAssets } },
      { type: 'SOURCE_OF_INCOME', value: sourceOfIncome },
    );
  }

  return { answers };
};

export const transformCompanyFormDataToSurveyCommand = (
  data: CompanyOnboardingFormData,
): CompanyOnboardingSurveyCommand => {
  const answers: CompanyOnboardingSurveyCommand['answers'] = [];

  if (data.registryLookup) {
    answers.push({
      type: 'BUSINESS_REGISTRY_NUMBER',
      value: { type: 'TEXT', value: data.registryLookup.registryNumber },
    });
  }

  let resolvedAddress: Address;
  if (data.companyAddress.reuseBackendAddress) {
    const validatedAddress = data.companyValidatedData?.address.value;
    resolvedAddress = {
      fullAddress: validatedAddress?.fullAddress,
      street: validatedAddress?.street ?? '',
      city: validatedAddress?.city ?? '',
      postalCode: validatedAddress?.postalCode ?? '',
      countryCode: (validatedAddress?.countryCode ?? '') as ISO2CountryCode,
    };
  } else {
    resolvedAddress = data.companyAddress.address;
  }

  answers.push({
    type: 'COMPANY_ADDRESS',
    value: { type: 'ADDRESS', value: resolvedAddress },
  });

  if (data.investmentGoals) {
    answers.push({ type: 'INVESTMENT_GOALS', value: data.investmentGoals });
  }

  if (data.investableAssets) {
    answers.push({
      type: 'INVESTABLE_ASSETS',
      value: { type: 'OPTION', value: data.investableAssets },
    });
  }

  const selectedIncomeSources = Object.entries(data.sourceOfCompanyIncome).map(([key]) => ({
    type: 'OPTION' as const,
    value: key as CompanySourceOfIncomeOption,
  }));
  answers.push({ type: 'COMPANY_SOURCE_OF_INCOME', value: selectedIncomeSources });

  return { answers };
};
